import { useState } from "react";
import { Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

export default function AiInsightsPanel() {
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState("");
    const [stats, setStats] = useState(null);
    const [generatedAt, setGeneratedAt] = useState(null);

    const run = async () => {
        setLoading(true);
        try {
            const r = await http.post("/ai/insights");
            setSummary(r.data.summary);
            setStats(r.data.stats);
            setGeneratedAt(r.data.generated_at);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not generate insights");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card-surface p-5" data-testid="ai-insights-panel">
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-emerald-400" />
                        <h2 className="text-lg font-bold text-white">AI Weekly Briefing</h2>
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                        Claude-generated analyst summary of the last 7 days of leads. No PII sent.
                    </p>
                </div>
                <button
                    data-testid="ai-insights-generate-btn"
                    onClick={run}
                    disabled={loading}
                    className="shrink-0 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-slate-950 text-sm font-semibold rounded-lg px-3.5 py-2 transition"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    {loading ? "Analyzing…" : summary ? "Regenerate" : "Generate briefing"}
                </button>
            </div>

            {!summary && !loading && (
                <div className="text-sm text-slate-500 italic py-8 text-center border border-dashed border-white/10 rounded-lg">
                    Click "Generate briefing" to get an AI analysis of this week's leads.
                </div>
            )}

            {summary && (
                <div className="space-y-3">
                    <pre
                        data-testid="ai-insights-summary"
                        className="whitespace-pre-wrap text-sm text-slate-200 leading-relaxed font-sans bg-black/20 border border-white/5 rounded-lg p-4"
                    >
                        {summary}
                    </pre>
                    {stats && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                            <div className="bg-white/5 rounded-lg p-2.5">
                                <div className="text-slate-500 uppercase tracking-wider text-[10px]">7-day leads</div>
                                <div className="text-xl font-bold text-emerald-400">{stats.leads_last_7_days}</div>
                            </div>
                            <div className="bg-white/5 rounded-lg p-2.5">
                                <div className="text-slate-500 uppercase tracking-wider text-[10px]">All time</div>
                                <div className="text-xl font-bold text-white">{stats.total_leads_all_time}</div>
                            </div>
                            <div className="bg-white/5 rounded-lg p-2.5 col-span-2">
                                <div className="text-slate-500 uppercase tracking-wider text-[10px] mb-1">Top ZIPs (7d)</div>
                                <div className="text-slate-200 font-mono text-xs">
                                    {stats.top_zip_codes_7d?.length
                                        ? stats.top_zip_codes_7d.map(([z, n]) => `${z} (${n})`).join(" · ")
                                        : "—"}
                                </div>
                            </div>
                        </div>
                    )}
                    {generatedAt && (
                        <div className="text-[10px] text-slate-500">
                            Generated {new Date(generatedAt).toLocaleString()}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
